import { Fragment } from "react/jsx-runtime";
import type { StreamMessage } from "../../hooks/useStreamingText";
import type { ChatTool } from "./ChatTool";
import type { GroupedEntry } from "./Transcript";
import { ToolUseGroup } from "./ToolUseGroup";
import { Message } from "./transcriptStyles";

type Props = {
  grouped: Extract<GroupedEntry, { type: "toolGroup" }>;
  prevGrouped: GroupedEntry | undefined;
  nextGrouped: GroupedEntry | undefined;
  dateMarker: React.ReactNode;
  expandedToolGroups: Set<string>;
  setExpandedToolGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  expandedToolUse: string | null;
  setExpandedToolUse: React.Dispatch<React.SetStateAction<string | null>>;
  toolsByName: Map<string, ChatTool>;
  streamingEntries: ReadonlyMap<string, StreamMessage>;
  onProvideToolResult: (
    entryId: string,
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean }
  ) => void;
};

export function ToolGroupRow({
  grouped,
  prevGrouped,
  nextGrouped,
  dateMarker,
  expandedToolGroups,
  setExpandedToolGroups,
  expandedToolUse,
  setExpandedToolUse,
  toolsByName,
  streamingEntries,
  onProvideToolResult,
}: Props) {
  const isReasoningOnly =
    grouped.entries.length === 0 && !!grouped.reasoning?.assistantReasoning;
  const nextEntry =
    nextGrouped?.type === "single" ? nextGrouped.entry : undefined;
  const nextIsAssistantMessage = nextEntry?.entryType === "AssistantMessage";
  const prevIsUserMessage =
    prevGrouped?.type === "single" &&
    prevGrouped.entry.entryType === "UserMessage";
  const toolGroupMarginBottom =
    nextGrouped && isReasoningOnly && nextIsAssistantMessage
      ? 12
      : nextGrouped
        ? 32
        : 0;
  const toolGroupMarginTop = isReasoningOnly && prevIsUserMessage ? 12 : 0;

  return (
    <Fragment>
      {dateMarker}
      <Message
        style={{
          marginBottom: toolGroupMarginBottom,
          ...(toolGroupMarginTop > 0 && {
            marginTop: toolGroupMarginTop,
          }),
        }}
      >
        <ToolUseGroup
          entries={grouped.entries}
          reasoning={grouped.reasoning}
          collapsed={
            grouped.isTrailing
              ? false
              : !expandedToolGroups.has(grouped.groupId)
          }
          toggleCollapsed={() =>
            setExpandedToolGroups((prev) => {
              const next = new Set(prev);
              if (next.has(grouped.groupId)) {
                next.delete(grouped.groupId);
              } else {
                next.add(grouped.groupId);
              }
              return next;
            })
          }
          expandedToolUse={expandedToolUse}
          setExpandedToolUse={setExpandedToolUse}
          tools={toolsByName}
          isTrailing={grouped.isTrailing}
          onProvideResult={onProvideToolResult}
          streamingEntries={streamingEntries}
        />
      </Message>
    </Fragment>
  );
}
